import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { Card } from '../game/data/cards/types';
import { CardTile, cardAspectClass, type CardSize } from './CardTile';
import { Rail, railItemStyle, type RailHandle } from './Rail';
import { Explain } from './Explain';
import { useLayoutMode, type ScreenMode } from './useLayoutMode';

// The hand rail — a bottom-anchored, horizontally scrollable strip of the
// player's bought-but-unplayed cards.
//
// Three properties drive the whole design:
//
// 1. IT IS A PORTAL, NOT A COLUMN CHILD.
//    The hand used to be a flex sibling of shop and board, permanently
//    consuming ~22% of viewport height even when empty. That's what made
//    the vertical budget impossible on phones. Rendering into document.body
//    escapes the tavern's `overflow-hidden` and every ancestor stacking
//    context (including the z-40 section lift used by hero-power
//    targeting), so the rail floats above the layout instead of competing
//    with it.
//
// 2. GESTURE ARBITRATION IS THE BROWSER'S JOB.
//    Swipe-to-browse and drag-to-play are both one-finger gestures on the
//    same card. Rather than gate drags behind a long-press delay, cards
//    declare `touch-action: pan-x`: the browser claims horizontal movement
//    for native scrolling (momentum, rubber-banding, correct platform
//    feel) and hands everything else to dnd-kit. When the browser commits
//    to a horizontal pan mid-gesture it fires pointercancel, which dnd-kit
//    already handles — the half-started drag aborts cleanly.
//    Net: swipe sideways = browse, pull up = play.
//
// 3. IT YIELDS ONLY WHILE DRAGGING.
//    At rest the rail is top-most and unblockable. But it sits over the
//    board's lower row, and the sell strip shares its band — so any active
//    drag slides it out of the way. The DragOverlay carries a clone of the
//    lifted card, so nothing is lost visually.

/** Height the rail occupies at rest, so the tavern reserves the band and no
    interactive chrome ends up parked underneath it. Also the height rail
    cards resolve `h-full` against.
    The floor is deliberately low: a 360px-tall landscape phone can't spare
    96px, and the rail's cards are square icons at that size. */
export const HAND_RAIL_CLEARANCE = 'clamp(74px, 14vh, 168px)';

/** Portrait phones have the height budget for a taller band (bigger, more
    readable cards); landscape keeps the low floor above. This also absorbs
    part of portrait's vertical surplus so the shop and board rails don't
    have to stretch their cards to fill it. */
const HAND_RAIL_CLEARANCE_PORTRAIT = 'clamp(96px, 20vh, 200px)';

/** Mode-aware clearance — use this anywhere that reserves or anchors to the
    hand band (tavern bottom padding, the ActionBar's offset, this rail). */
export function handRailClearance(mode: ScreenMode): string {
  return mode === 'phonePortrait'
    ? HAND_RAIL_CLEARANCE_PORTRAIT
    : HAND_RAIL_CLEARANCE;
}

/** Sits above the sell strip (20) and lifted sections (40), below modals
    (50). Modals are exclusive-focus states where the hand shouldn't be
    interactive at all — see `suppressed`. */
const RAIL_Z = 45;

export interface HandRailProps {
  hand: Card[];
  /** Card content density — matches whatever the board/shop are using. */
  size: CardSize;
  /** Board has at least one empty slot; when false, cards can't be played. */
  canPlay: boolean;
  /**
   * Tap = select. The tavern's tap-select grammar: tapping a hand card
   * focuses it and surfaces Play/Info in the ActionBar, identical to how
   * shop and board cards behave. Nothing commits on a bare tap; playing
   * stays on drag (fast path) or the explicit Play button.
   */
  onTapSelect: (index: number) => void;
  /** Which hand index is currently selected — renders the sodium ring. */
  selectedIndex: number | null;
  /** True while any card is being dragged — rail slides out of the way. */
  dragging: boolean;
  /** True while a modal owns focus (discover, scouting, onboarding). */
  suppressed?: boolean;
  reducedMotion: boolean;
}

export function HandRail({
  hand,
  size,
  canPlay,
  onTapSelect,
  selectedIndex,
  dragging,
  suppressed = false,
  reducedMotion,
}: HandRailProps) {
  const railRef = useRef<RailHandle | null>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const prevLenRef = useRef(hand.length);
  const { mode } = useLayoutMode();
  const clearance = handRailClearance(mode);

  // Newly bought cards append to the end — scroll to reveal the arrival so
  // a purchase never lands off-screen. Pairs with the golden-triple sodium
  // flash that fires on card mount.
  useEffect(() => {
    const grew = hand.length > prevLenRef.current;
    prevLenRef.current = hand.length;
    if (!grew) return;
    railRef.current?.scrollToEnd(!reducedMotion);
  }, [hand.length, reducedMotion]);

  if (typeof document === 'undefined') return null;

  const hidden = suppressed || dragging;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0"
      style={{
        zIndex: RAIL_Z,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <motion.div
        animate={{
          y: hidden ? '110%' : '0%',
          opacity: hidden ? 0 : 1,
        }}
        initial={false}
        transition={{ duration: reducedMotion ? 0 : 0.18, ease: 'easeOut' }}
        className="pointer-events-auto relative"
      >
        {/* Scrim — lifts the rail off the board behind it without a hard
            edge, so the board stays readable through the gap. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 top-[-12px]"
          style={{
            background:
              'linear-gradient(to top, rgba(12,10,9,0.92) 0%, rgba(12,10,9,0.72) 55%, rgba(12,10,9,0) 100%)',
          }}
        />

        {/* Explicit height = the clearance the tavern reserves. This is what
            `h-full` on the rail cards resolves against, and it guarantees the
            rail occupies exactly the band the layout padded for. */}
        {/* items-stretch, NOT items-center: the Rail must inherit this box's
            height or the `h-full` chain down to the cards has nothing to
            resolve against and they collapse to zero width. */}
        <div
          className="relative flex items-stretch gap-xs px-xs py-[4px] sm:px-sm"
          style={{ height: clearance }}
        >
          <HandLabel
            count={hand.length}
            canPlay={canPlay}
            hasOverflow={overflow.left || overflow.right}
          />

          <Rail
            ref={railRef}
            snap
            label={`Hand, ${hand.length} cards`}
            onOverflowChange={setOverflow}
            className="items-stretch gap-xs py-[2px]"
          >
            {hand.length === 0 ? (
              <p className="py-md font-data text-[10px] uppercase tracking-[0.25em] text-graphite">
                empty hand — buy from shop
              </p>
            ) : (
              <AnimatePresence initial={false} mode="popLayout">
                {hand.map((card, i) => (
                  <RailCard
                    key={card.instanceId ?? `hand-${i}`}
                    card={card}
                    index={i}
                    size={size}
                    dragDisabled={!canPlay}
                    selected={selectedIndex === i}
                    onTap={() => onTapSelect(i)}
                    reducedMotion={reducedMotion}
                  />
                ))}
              </AnimatePresence>
            )}
          </Rail>
        </div>
      </motion.div>

    </div>,
    document.body,
  );
}

function HandLabel({
  count,
  canPlay,
  hasOverflow,
}: {
  count: number;
  canPlay: boolean;
  hasOverflow: boolean;
}) {
  return (
    <Explain
      id="tavern.hand"
      side="top"
      detail={
        !canPlay
          ? 'Board is full — sell or play nothing this turn'
          : 'Tap a card for actions, or drag it up to play'
      }
      className="flex shrink-0 flex-col items-start justify-center gap-[1px] pr-[2px]"
    >
      <span className="font-data text-[10px] uppercase tracking-[0.25em] text-marker">
        Hand
      </span>
      <span
        className={`font-data text-[11px] font-bold sm:text-[13px] ${
          count >= 10 ? 'text-aerosolRed' : 'text-bone'
        }`}
      >
        {count}
        <span className="text-graphite">/10</span>
      </span>
      {/* Hints, most-urgent first. "tap · drag" is load-bearing on touch:
          without it neither the select grammar nor the play gesture is
          discoverable. 10px floor — this text has to be readable. */}
      {!canPlay ? (
        <span className="font-data text-[10px] uppercase tracking-[0.15em] text-aerosolRed">
          board full
        </span>
      ) : (
        <span className="font-data text-[10px] uppercase tracking-[0.15em] text-graphite">
          {hasOverflow ? 'tap · drag · swipe' : 'tap · drag'}
        </span>
      )}
    </Explain>
  );
}

// One card in the rail. Draggable via dnd-kit for precise slot placement,
// tappable for the fast "play to first empty slot" path.
//
// `touch-action: pan-x` is the load-bearing line — see the module comment.
// It must NOT be `none` (which is what a fanned hand needed), or the
// browser hands every gesture to dnd-kit and the rail stops scrolling.
function RailCard({
  card,
  index,
  size,
  dragDisabled,
  selected,
  onTap,
  reducedMotion,
}: {
  card: Card;
  index: number;
  size: CardSize;
  dragDisabled: boolean;
  selected: boolean;
  onTap: () => void;
  reducedMotion: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `hand:${index}`,
      disabled: dragDisabled,
      data: { kind: 'hand', index },
    });

  // Golden arrivals (triple result) flash a sodium ring so the moment
  // lands even though the card may scroll into view rather than appear.
  const isGolden = card.isGolden === true;
  const entryDuration = reducedMotion ? 0 : isGolden ? 0.55 : 0.22;

  return (
    <motion.div
      ref={setNodeRef}
      role="listitem"
      layout={!reducedMotion}
      initial={{ opacity: 0, y: 12 }}
      animate={{
        opacity: isDragging ? 0.2 : 1,
        y: 0,
        boxShadow: isGolden
          ? [
              '0 0 0px 0px rgba(255,138,61,0)',
              '0 0 22px 8px rgba(255,138,61,0.65)',
              '0 0 0px 0px rgba(255,138,61,0)',
            ]
          : '0 0 0px 0px rgba(255,138,61,0)',
      }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: entryDuration, ease: 'easeOut' }}
      style={{
        transform: CSS.Translate.toString(transform),
        ...railItemStyle(),
        borderRadius: '8px',
      }}
      // Height-driven like every other rail: the clearance sets the height,
      // the aspect ratio sets the width. Cards can't overflow the band no
      // matter which size tier is active.
      className={`h-full shrink-0 snap-start ${cardAspectClass(size)}`}
      data-selkeep
      {...listeners}
      {...attributes}
    >
      <CardTile card={card} size={size} fill onClick={onTap} selected={selected} />
    </motion.div>
  );
}

